import type { SearchHit, TranscriptionRow, TranscriptionSummary } from "./types";

const JSON_HEADERS = { accept: "application/json" };

async function check(res: Response): Promise<Response> {
  if (res.status === 401) {
    window.location.href = "/login";
    throw new Error("unauthorized");
  }
  if (!res.ok) throw new Error(`${res.status} ${res.statusText}`);
  return res;
}

export async function listTranscriptions(): Promise<TranscriptionSummary[]> {
  const r = await fetch("/api/transcriptions", { headers: JSON_HEADERS }).then(check);
  return r.json();
}

export async function getTranscription(id: string): Promise<TranscriptionRow> {
  const r = await fetch(`/api/transcriptions/${id}`, { headers: JSON_HEADERS }).then(check);
  return r.json();
}

export async function searchTranscriptions(q: string): Promise<SearchHit[]> {
  const r = await fetch(`/api/search?q=${encodeURIComponent(q)}`, { headers: JSON_HEADERS }).then(check);
  return r.json();
}

export interface UploadResult {
  id: string;
  status: string;
  deduplicated?: boolean;
}

/** Upload with real progress feedback (fetch cannot report upload progress). */
export function uploadTranscription(
  file: File,
  onProgress?: (fraction: number) => void,
): Promise<UploadResult> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open("POST", "/api/transcriptions");
    xhr.responseType = "json";
    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable && onProgress) onProgress(e.loaded / e.total);
    };
    xhr.onload = () => {
      if (xhr.status === 401) {
        window.location.href = "/login";
        reject(new Error("unauthorized"));
        return;
      }
      if (xhr.status >= 200 && xhr.status < 300) {
        resolve(xhr.response as UploadResult);
      } else {
        const message = (xhr.response as { error?: string } | null)?.error;
        reject(new Error(message ?? `upload failed (${xhr.status})`));
      }
    };
    xhr.onerror = () => reject(new Error("network error during upload"));
    xhr.onabort = () => reject(new Error("upload cancelled"));
    const form = new FormData();
    form.append("file", file);
    xhr.send(form);
  });
}

export async function fetchMarkdown(id: string): Promise<string> {
  const r = await fetch(`/api/transcriptions/${id}/markdown`, { headers: JSON_HEADERS }).then(check);
  return r.text();
}

/** Clipboard write with a fallback for non-secure (plain http) contexts and denied permissions. */
export async function copyText(text: string): Promise<void> {
  if (navigator.clipboard?.writeText) {
    try {
      await navigator.clipboard.writeText(text);
      return;
    } catch {
      // fall through to the legacy path
    }
  }
  const ta = document.createElement("textarea");
  ta.value = text;
  ta.style.position = "fixed";
  ta.style.opacity = "0";
  document.body.appendChild(ta);
  ta.select();
  try {
    if (!document.execCommand("copy")) throw new Error("copy rejected");
  } finally {
    ta.remove();
  }
}

export async function retryTranscription(id: string): Promise<void> {
  await fetch(`/api/transcriptions/${id}/retry`, {
    method: "POST",
    headers: JSON_HEADERS,
  }).then(check);
}

export async function deleteTranscription(id: string): Promise<void> {
  await fetch(`/api/transcriptions/${id}`, {
    method: "DELETE",
    headers: JSON_HEADERS,
  }).then(check);
}

export async function updateMeta(
  id: string,
  patch: { title?: string; summary?: string },
): Promise<{ title: string | null; summary: string | null }> {
  const r = await fetch(`/api/transcriptions/${id}/meta`, {
    method: "POST",
    headers: { ...JSON_HEADERS, "content-type": "application/json" },
    body: JSON.stringify(patch),
  }).then(check);
  return r.json();
}

export async function setSpeakerLabel(id: string, speakerId: string, label: string): Promise<void> {
  const form = new FormData();
  form.append("speaker_id", speakerId);
  form.append("label", label);
  await fetch(`/api/transcriptions/${id}/speakers`, {
    method: "POST",
    headers: JSON_HEADERS,
    body: form,
  }).then(check);
}

export async function login(password: string, next: string): Promise<boolean> {
  const form = new FormData();
  form.append("password", password);
  form.append("next", next);
  const r = await fetch("/login", {
    method: "POST",
    headers: JSON_HEADERS,
    body: form,
    redirect: "manual",
  });
  return r.ok || r.status === 0 || r.status === 303;
}

export async function logout(): Promise<void> {
  await fetch("/logout", { method: "POST", headers: JSON_HEADERS });
  window.location.href = "/login";
}
